import {
  IconDeviceDesktop,
  IconDeviceMobile,
  IconDeviceTablet,
  IconRobot,
  IconSparkles,
  IconUser,
} from "@tabler/icons-react";

const READERS_TABLE_SIZES = {
  READER: "tw-grow",
  DEVICE: "tw-w-20",
  LOCATION: "tw-w-[136px]",
  SESSION_STARTED: "tw-w-[112px]",
  READING_TIME: "tw-w-[72px]",
  CLICKED_CTA: "tw-w-[64px]",
  PROGRESS: "tw-w-[146px]",
  ACTION: "tw-w-4",
} as const;

// ECharts tooltip formatters return an HTML string that ECharts injects as
// innerHTML, so every interpolated value is an XSS sink. Today both charts feed
// them closed label sets decided server-side (getDevice() returns only
// Desktop/Mobile/Tablet; TRAFFIC_TYPE's labels are SQL literals), so nothing is
// exploitable — but the same `events` row also carries `user_agent`, which is a
// raw attacker-controlled request header that keeps its angle brackets. Surface
// that, or any other free-text column, through a formatter and it becomes
// stored XSS. Escaping here is free: for every legitimate label the output is
// byte-identical.
const escapeHtml = (value: string) =>
  String(value).replace(
    /[&<>"']/g,
    (char) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[char] as string,
  );

const PIE_CHART_OPTIONS = {
  tooltip: {
    trigger: "item",
    backgroundColor: "#23272F",
    borderColor: "#23272F",
    borderWidth: 1,
    textStyle: {
      color: "#FFF",
      fontFamily: "Figtree",
      fontSize: 14,
      fontWeight: 500,
    },
    padding: 12,
    extraCssText: "box-shadow: 0 2px 8px rgba(0,0,0,0.15); border-radius: 8px;",
    formatter: (params: {
      marker: string;
      data: {
        device: string;
        value: number;
        sessions: number;
      };
    }) => `
        <div style='display:flex;align-items:center;gap:8px;'>
          ${params.marker}
          <span style='font-weight:500;'>${escapeHtml(params.data.device)}</span>
        </div>
        <div style='margin-top:4px;'>
          <span style='color:#C2C8D0; font-size:13px;'>Sessions:</span> <span style='font-weight:600;'>${Number(params.data.sessions).toLocaleString()}</span><br/>
          <span style='color:#C2C8D0; font-size:13px;'>Percentage:</span> <span style='font-weight:600;'>${params.data.value}%</span>
        </div>
      `,
  },
  legend: {
    show: false,
  },
  series: [
    {
      name: "Devices",
      type: "pie",
      radius: ["60%", "90%"],
      avoidLabelOverlap: false,
      label: {
        show: false,
      },
      emphasis: {
        label: {
          show: false,
        },
      },
      labelLine: {
        show: true,
        length: 10,
        length2: 10,
      },
    },
  ],
};

const PIE_CHART_DEVICE_MAP = {
  Desktop: {
    itemStyle: { color: "#610CFF" },
    icon: IconDeviceDesktop,
  },
  Tablet: {
    itemStyle: { color: "#9573FF" },
    icon: IconDeviceTablet,
  },
  Mobile: {
    itemStyle: { color: "#D5CDFF" },
    icon: IconDeviceMobile,
  },
} as const;

const PIE_CHART_TRAFFIC_MAP = {
  Human: {
    itemStyle: { color: "#610CFF" },
    icon: IconUser,
  },
  AI: {
    itemStyle: { color: "#9573FF" },
    icon: IconSparkles,
  },
  "Other bots": {
    itemStyle: { color: "#D5CDFF" },
    icon: IconRobot,
  },
} as const;

// Derived from PIE_CHART_OPTIONS rather than duplicating it, so the donut
// geometry and tooltip chrome stay identical to the Devices chart. Only the two
// parts that name the dimension are overridden: PIE_CHART_OPTIONS is shared
// with Devices and its formatter reads `data.device`, which is undefined here.
const PIE_CHART_TRAFFIC_OPTIONS = {
  ...PIE_CHART_OPTIONS,
  tooltip: {
    ...PIE_CHART_OPTIONS.tooltip,
    formatter: (params: {
      marker: string;
      data: {
        type: string;
        value: number;
        sessions: number;
      };
    }) => `
        <div style='display:flex;align-items:center;gap:8px;'>
          ${params.marker}
          <span style='font-weight:500;'>${escapeHtml(params.data.type)}</span>
        </div>
        <div style='margin-top:4px;'>
          <span style='color:#C2C8D0; font-size:13px;'>Sessions:</span> <span style='font-weight:600;'>${Number(params.data.sessions).toLocaleString()}</span><br/>
          <span style='color:#C2C8D0; font-size:13px;'>Percentage:</span> <span style='font-weight:600;'>${params.data.value}%</span>
        </div>
      `,
  },
  series: [{ ...PIE_CHART_OPTIONS.series[0], name: "Traffic type" }],
};

const COLORS = [
  "#610CFF", // Purple
  "#4BB2FF", // Blue
  "#FF6B6B", // Red
  "#4ECDC4", // Teal
  "#45B7D1", // Light Blue
  "#96CEB4", // Mint Green
  "#FFEAA7", // Yellow
  "#DDA0DD", // Plum
  "#98D8C8", // Sea Green
  "#F7DC6F", // Golden Yellow
  "#BB8FCE", // Lavender
  "#85C1E9", // Sky Blue
];

const PROGRESS_COLORS = {
  GREEN: "#44A20E",
  ORANGE: "#E29A00",
  RED: "#E5281D",
} as const;

const PROGRESS_THRESHOLDS = {
  VERSION_DEPTH_FUNNEL: {
    GREEN: 51,
    ORANGE: 21,
  },
  SESSIONS_TABLE: {
    GREEN: 51,
    ORANGE: 21,
  },
  SESSION_DEPTH_FUNNEL: {
    GREEN: 5,
    ORANGE: 2,
  },
} as const;

// =========== NEW CONSTANTS ===========

export enum ANALYTICS_QUERY {
  STATS = "STATS",
  READING_DEPTH = "READING_DEPTH",
  READERS_BY_LOCATION = "READERS_BY_LOCATION",
  SESSIONS_BY_LOCATION = "SESSIONS_BY_LOCATION",
  READERS_BY_DATE = "READERS_BY_DATE",
  SESSIONS_BY_DATE = "SESSIONS_BY_DATE",
  VERSIONS = "VERSIONS",
  TOP_ACTIONS = "TOP_ACTIONS",
  DEVICES = "DEVICES",
  TRAFFIC_TYPE = "TRAFFIC_TYPE",
  ENGAGEMENT_FUNNEL = "ENGAGEMENT_FUNNEL",
  READERS = "READERS",
  SESSION_READING_DEPTH = "SESSION_READING_DEPTH",
  CSV = "CSV",
}

const VERSIONS_TABLE_SIZES = {
  READERS: 88,
  AVG_READING_TIME: 112,
  ENGAGED_USERS: 112,
  AVG_PROGRESS: 128,
  CREATED_AT: 144,
  OWNER: 96,
} as const;

const SESSIONS_TABLE_SIZES = {
  SESSION_STARTED: 128,
  READING_TIME: 96,
  CLICKED_CTA: 96,
  PROGRESS: 128,
  LOCATION: 148,
  DEVICE: 98,
} as const;

const VERSIONS_TABLE_SESSIONS_SIZES = {
  SESSION_STARTED: 136,
  READING_TIME: 112,
  CLICKED_CTA: 112,
  PROGRESS: 128,
  LOCATION: 136,
  DEVICE: 144,
} as const;

const COUNTRY_FLAGS: Record<string, string> = {
  Afghanistan: "🇦🇫",
  Albania: "🇦🇱",
  Algeria: "🇩🇿",
  Andorra: "🇦🇩",
  Angola: "🇦🇴",
  "Antigua and Barbuda": "🇦🇬",
  Argentina: "🇦🇷",
  Armenia: "🇦🇲",
  Australia: "🇦🇺",
  Austria: "🇦🇹",
  Azerbaijan: "🇦🇿",
  Bahamas: "🇧🇸",
  Bahrain: "🇧🇭",
  Bangladesh: "🇧🇩",
  Barbados: "🇧🇧",
  Belarus: "🇧🇾",
  Belgium: "🇧🇪",
  Belize: "🇧🇿",
  Benin: "🇧🇯",
  Bhutan: "🇧🇹",
  Bolivia: "🇧🇴",
  "Bosnia and Herzegovina": "🇧🇦",
  Botswana: "🇧🇼",
  Brazil: "🇧🇷",
  Brunei: "🇧🇳",
  Bulgaria: "🇧🇬",
  "Burkina Faso": "🇧🇫",
  Burundi: "🇧🇮",
  Cambodia: "🇰🇭",
  Cameroon: "🇨🇲",
  Canada: "🇨🇦",
  "Cape Verde": "🇨🇻",
  "Central African Republic": "🇨🇫",
  Chad: "🇹🇩",
  Chile: "🇨🇱",
  China: "🇨🇳",
  Colombia: "🇨🇴",
  Comoros: "🇰🇲",
  "Congo (Brazzaville)": "🇨🇬",
  "Congo (Kinshasa)": "🇨🇩",
  "Costa Rica": "🇨🇷",
  Croatia: "🇭🇷",
  Cuba: "🇨🇺",
  Cyprus: "🇨🇾",
  "Czech Republic": "🇨🇿",
  Czechia: "🇨🇿",
  Denmark: "🇩🇰",
  Djibouti: "🇩🇯",
  Dominica: "🇩🇲",
  "Dominican Republic": "🇩🇴",
  Ecuador: "🇪🇨",
  Egypt: "🇪🇬",
  "El Salvador": "🇸🇻",
  "Equatorial Guinea": "🇬🇶",
  Eritrea: "🇪🇷",
  Estonia: "🇪🇪",
  Eswatini: "🇸🇿",
  Ethiopia: "🇪🇹",
  Fiji: "🇫🇯",
  Finland: "🇫🇮",
  France: "🇫🇷",
  Gabon: "🇬🇦",
  Gambia: "🇬🇲",
  Georgia: "🇬🇪",
  Germany: "🇩🇪",
  Ghana: "🇬🇭",
  Greece: "🇬🇷",
  Grenada: "🇬🇩",
  Guatemala: "🇬🇹",
  Guinea: "🇬🇳",
  "Guinea-Bissau": "🇬🇼",
  Guyana: "🇬🇾",
  Haiti: "🇭🇹",
  Honduras: "🇭🇳",
  Hungary: "🇭🇺",
  Iceland: "🇮🇸",
  India: "🇮🇳",
  Indonesia: "🇮🇩",
  Iran: "🇮🇷",
  Iraq: "🇮🇶",
  Ireland: "🇮🇪",
  Israel: "🇮🇱",
  Italy: "🇮🇹",
  Jamaica: "🇯🇲",
  Japan: "🇯🇵",
  Jordan: "🇯🇴",
  Kazakhstan: "🇰🇿",
  Kenya: "🇰🇪",
  Kiribati: "🇰🇮",
  "North Korea": "🇰🇵",
  "South Korea": "🇰🇷",
  Kuwait: "🇰🇼",
  Kyrgyzstan: "🇰🇬",
  Laos: "🇱🇦",
  Latvia: "🇱🇻",
  Lebanon: "🇱🇧",
  Lesotho: "🇱🇸",
  Liberia: "🇱🇷",
  Libya: "🇱🇾",
  Liechtenstein: "🇱🇮",
  Lithuania: "🇱🇹",
  Luxembourg: "🇱🇺",
  Madagascar: "🇲🇬",
  Malawi: "🇲🇼",
  Malaysia: "🇲🇾",
  Maldives: "🇲🇻",
  Mali: "🇲🇱",
  Malta: "🇲🇹",
  "Marshall Islands": "🇲🇭",
  Mauritania: "🇲🇷",
  Mauritius: "🇲🇺",
  Mexico: "🇲🇽",
  Micronesia: "🇫🇲",
  Moldova: "🇲🇩",
  Monaco: "🇲🇨",
  Mongolia: "🇲🇳",
  Montenegro: "🇲🇪",
  Morocco: "🇲🇦",
  Mozambique: "🇲🇿",
  Myanmar: "🇲🇲",
  Namibia: "🇳🇦",
  Nauru: "🇳🇷",
  Nepal: "🇳🇵",
  Netherlands: "🇳🇱",
  "New Zealand": "🇳🇿",
  Nicaragua: "🇳🇮",
  Niger: "🇳🇪",
  Nigeria: "🇳🇬",
  "North Macedonia": "🇲🇰",
  Norway: "🇳🇴",
  Oman: "🇴🇲",
  Pakistan: "🇵🇰",
  Palau: "🇵🇼",
  Palestine: "🇵🇸",
  Panama: "🇵🇦",
  "Papua New Guinea": "🇵🇬",
  Paraguay: "🇵🇾",
  Peru: "🇵🇪",
  Philippines: "🇵🇭",
  Poland: "🇵🇱",
  Portugal: "🇵🇹",
  Qatar: "🇶🇦",
  Romania: "🇷🇴",
  Russia: "🇷🇺",
  Rwanda: "🇷🇼",
  "Saint Kitts and Nevis": "🇰🇳",
  "Saint Lucia": "🇱🇨",
  "Saint Vincent and the Grenadines": "🇻🇨",
  Samoa: "🇼🇸",
  "San Marino": "🇸🇲",
  "Sao Tome and Principe": "🇸🇹",
  "Saudi Arabia": "🇸🇦",
  Senegal: "🇸🇳",
  Serbia: "🇷🇸",
  Seychelles: "🇸🇨",
  "Sierra Leone": "🇸🇱",
  Singapore: "🇸🇬",
  Slovakia: "🇸🇰",
  Slovenia: "🇸🇮",
  "Solomon Islands": "🇸🇧",
  Somalia: "🇸🇴",
  "South Africa": "🇿🇦",
  "South Sudan": "🇸🇸",
  Spain: "🇪🇸",
  "Sri Lanka": "🇱🇰",
  Sudan: "🇸🇩",
  Suriname: "🇸🇷",
  Sweden: "🇸🇪",
  Switzerland: "🇨🇭",
  Syria: "🇸🇾",
  Taiwan: "🇹🇼",
  Tajikistan: "🇹🇯",
  Tanzania: "🇹🇿",
  Thailand: "🇹🇭",
  "Timor-Leste": "🇹🇱",
  Togo: "🇹🇬",
  Tonga: "🇹🇴",
  "Trinidad and Tobago": "🇹🇹",
  Tunisia: "🇹🇳",
  Turkey: "🇹🇷",
  Turkmenistan: "🇹🇲",
  Tuvalu: "🇹🇻",
  Uganda: "🇺🇬",
  Ukraine: "🇺🇦",
  "United Arab Emirates": "🇦🇪",
  UAE: "🇦🇪",
  "United Kingdom": "🇬🇧",
  UK: "🇬🇧",
  "United States": "🇺🇸",
  USA: "🇺🇸",
  US: "🇺🇸",
  Uruguay: "🇺🇾",
  Uzbekistan: "🇺🇿",
  Vanuatu: "🇻🇺",
  "Vatican City": "🇻🇦",
  Venezuela: "🇻🇪",
  Vietnam: "🇻🇳",
  Yemen: "🇾🇪",
  Zambia: "🇿🇲",
  Zimbabwe: "🇿🇼",
};
// =========== NEW CONSTANTS ===========

export {
  COLORS,
  COUNTRY_FLAGS,
  PIE_CHART_DEVICE_MAP,
  PIE_CHART_OPTIONS,
  PIE_CHART_TRAFFIC_MAP,
  PIE_CHART_TRAFFIC_OPTIONS,
  PROGRESS_COLORS,
  PROGRESS_THRESHOLDS,
  READERS_TABLE_SIZES,
  SESSIONS_TABLE_SIZES,
  VERSIONS_TABLE_SESSIONS_SIZES,
  VERSIONS_TABLE_SIZES,
};
